/**
 * Blog Yönetimi Servis Katmanı
 *
 * Personel paneli için blog CRUD işlemlerini ve
 * public blog görüntüleme işlemlerini yönetir.
 * Supabase client üzerinden RLS korumalı sorgular çalıştırır.
 *
 * @module blogService
 */

import { supabase } from '../lib/supabase';

const blogService = {
  // ===========================================================
  // Blog Listesi (Staff)
  // ===========================================================

  /**
   * Tüm blog yazılarını getirir (staff paneli için).
   * Filtreleme ve sayfalama desteği vardır.
   * @param {Object} filters - Filtreleme seçenekleri
   * @param {string} [filters.status] - draft | review | published | archived
   * @param {string} [filters.author_id] - Yazar UUID
   * @param {string} [filters.category] - Kategori
   * @param {string} [filters.search] - Başlık veya içerik arama
   * @param {number} [filters.limit=20] - Sayfa başına kayıt sayısı
   * @param {number} [filters.offset=0] - Kaydırma miktarı
   * @param {string} [filters.sortBy='created_at'] - Sıralama alanı
   * @param {boolean} [filters.ascending=false] - Artan sıralama
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getBlogPosts(filters = {}) {
    try {
      const {
        status,
        author_id,
        category,
        search,
        limit = 20,
        offset = 0,
        sortBy = 'created_at',
        ascending = false,
      } = filters;

      let query = supabase
        .from('blog_posts')
        .select('*', { count: 'exact' });

      // Filtreler
      if (status) {
        query = query.eq('status', status);
      }
      if (author_id) {
        query = query.eq('author_id', author_id);
      }
      if (category) {
        query = query.eq('category', category);
      }
      if (search) {
        query = query.or(`title.ilike.%${search}%,title_tr.ilike.%${search}%,title_en.ilike.%${search}%,excerpt.ilike.%${search}%`);
      }

      // Sıralama
      query = query.order(sortBy, { ascending });

      // Sayfalama
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      return { data, count, error };
    } catch (error) {
      console.error('getBlogPosts error:', error);
      return { data: null, count: 0, error };
    }
  },

  // ===========================================================
  // Public Blog Listesi
  // ===========================================================

  /**
   * Yayınlanmış blog yazılarını getirir (public sayfa için).
   * @param {number} [limit=10] - Maksimum kayıt sayısı
   * @param {number} [offset=0] - Kaydırma miktarı
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getPublishedBlogPosts(limit = 10, offset = 0) {
    try {
      const { data, error, count } = await supabase
        .from('blog_posts')
        .select(`
          id,
          title,
          slug,
          excerpt,
          featured_image,
          category,
          tags,
          title_tr,
          title_en,
          title_ar,
          title_ru,
          excerpt_tr,
          excerpt_en,
          excerpt_ar,
          excerpt_ru,
          meta_title,
          meta_description,
          published_at,
          created_at
        `, { count: 'exact' })
        .eq('status', 'published')
        .lte('published_at', new Date().toISOString())
        .order('published_at', { ascending: false })
        .range(offset, offset + limit - 1);

      return { data, count, error };
    } catch (error) {
      console.error('getPublishedBlogPosts error:', error);
      return { data: null, count: 0, error };
    }
  },

  // ===========================================================
  // Tek Blog Yazısı
  // ===========================================================

  /**
   * Tek bir blog yazısını ID ile getirir.
   * @param {string} blogId - Blog UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async getBlogPostById(blogId) {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', blogId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('getBlogPostById error:', error);
      return { data: null, error };
    }
  },

  /**
   * Blog yazısını slug ile getirir (SEO-friendly URL).
   * @param {string} slug - Blog slug'ı
   * @returns {{ data: object|null, error: object|null }}
   */
  async getBlogPostBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .single();

      return { data, error };
    } catch (error) {
      console.error('getBlogPostBySlug error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Blog Oluştur
  // ===========================================================

  /**
   * Yeni bir blog yazısı oluşturur.
   * Slug otomatik olarak veritabanı trigger'ı tarafından oluşturulur.
   * @param {Object} blogData - Blog verileri
   * @param {string} blogData.title - Başlık (zorunlu)
   * @param {string} blogData.content - İçerik (zorunlu)
   * @param {string} [blogData.slug] - Slug (boş bırakılırsa otomatik oluşur)
   * @param {string} [blogData.excerpt] - Özet
   * @param {string} [blogData.featured_image] - Öne çıkan görsel URL
   * @param {string} [blogData.status] - draft | review | published | archived
   * @param {string} [blogData.category] - Kategori
   * @param {Array} [blogData.tags] - Etiketler
   * @param {string} [blogData.title_tr] - Türkçe başlık
   * @param {string} [blogData.title_en] - İngilizce başlık
   * @param {string} [blogData.title_ar] - Arapça başlık
   * @param {string} [blogData.title_ru] - Rusça başlık
   * @param {string} [blogData.content_tr] - Türkçe içerik
   * @param {string} [blogData.content_en] - İngilizce içerik
   * @param {string} [blogData.content_ar] - Arapça içerik
   * @param {string} [blogData.content_ru] - Rusça içerik
   * @param {string} [blogData.excerpt_tr] - Türkçe özet
   * @param {string} [blogData.excerpt_en] - İngilizce özet
   * @param {string} [blogData.excerpt_ar] - Arapça özet
   * @param {string} [blogData.excerpt_ru] - Rusça özet
   * @param {string} [blogData.meta_title] - SEO başlık
   * @param {string} [blogData.meta_description] - SEO açıklama
   * @returns {{ data: object|null, error: object|null }}
   */
  async createBlogPost(blogData) {
    try {
      const insertData = {
        title: blogData.title,
        content: blogData.content,
        status: blogData.status || 'draft',
      };

      // Opsiyonel alanlar
      const optionalFields = [
        'slug', 'excerpt', 'featured_image', 'category', 'tags',
        'title_tr', 'title_en', 'title_ar', 'title_ru',
        'content_tr', 'content_en', 'content_ar', 'content_ru',
        'excerpt_tr', 'excerpt_en', 'excerpt_ar', 'excerpt_ru',
        'meta_title', 'meta_description', 'author_id',
      ];

      optionalFields.forEach(field => {
        if (blogData[field] !== undefined) {
          insertData[field] = blogData[field];
        }
      });

      // Eğer published olarak oluşturuluyorsa published_at set et
      if (insertData.status === 'published' && !blogData.published_at) {
        insertData.published_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('blog_posts')
        .insert(insertData)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('createBlogPost error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Blog Güncelle
  // ===========================================================

  /**
   * Mevcut bir blog yazısını günceller.
   * @param {string} blogId - Blog UUID'si
   * @param {Object} blogData - Güncellenecek veriler
   * @returns {{ data: object|null, error: object|null }}
   */
  async updateBlogPost(blogId, blogData) {
    try {
      const allowedFields = [
        'title', 'slug', 'content', 'excerpt', 'featured_image',
        'status', 'category', 'tags',
        'title_tr', 'title_en', 'title_ar', 'title_ru',
        'content_tr', 'content_en', 'content_ar', 'content_ru',
        'excerpt_tr', 'excerpt_en', 'excerpt_ar', 'excerpt_ru',
        'meta_title', 'meta_description', 'published_at',
      ];

      const updateData = {};
      allowedFields.forEach(field => {
        if (blogData[field] !== undefined) {
          updateData[field] = blogData[field];
        }
      });

      const { data, error } = await supabase
        .from('blog_posts')
        .update(updateData)
        .eq('id', blogId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('updateBlogPost error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Blog Sil
  // ===========================================================

  /**
   * Bir blog yazısını siler.
   * @param {string} blogId - Blog UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async deleteBlogPost(blogId) {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', blogId);

      return { data, error };
    } catch (error) {
      console.error('deleteBlogPost error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Blog Durum Değişiklikleri
  // ===========================================================

  /**
   * Blog yazısını yayınlar. Status = published, published_at = now().
   * @param {string} blogId - Blog UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async publishBlogPost(blogId) {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
        })
        .eq('id', blogId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('publishBlogPost error:', error);
      return { data: null, error };
    }
  },

  /**
   * Blog yazısını arşivler. Status = archived.
   * @param {string} blogId - Blog UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async archiveBlogPost(blogId) {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .update({ status: 'archived' })
        .eq('id', blogId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('archiveBlogPost error:', error);
      return { data: null, error };
    }
  },

  /**
   * Blog yazısını taslak durumuna çevirir. Status = draft.
   * @param {string} blogId - Blog UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async unpublishBlogPost(blogId) {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .update({ status: 'draft' })
        .eq('id', blogId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('unpublishBlogPost error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Blog Görsel Yükleme
  // ===========================================================

  /**
   * Blog görseli yükler.
   * @param {File} file - Yüklenecek dosya
   * @returns {{ data: string|null, error: object|null }}
   */
  async uploadBlogImage(file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `blog/${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;

      const { data: storageData, error: storageError } = await supabase.storage
        .from('property-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (storageError) {
        return { data: null, error: storageError };
      }

      // Public URL al
      const { data: urlData } = supabase.storage
        .from('property-images')
        .getPublicUrl(storageData.path);

      return { data: urlData.publicUrl, error: null };
    } catch (error) {
      console.error('uploadBlogImage error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Kategoriler ve Etiketler
  // ===========================================================

  /**
   * Kullanılan tüm kategorileri getirir (distinct).
   * @returns {{ data: array|null, error: object|null }}
   */
  async getCategories() {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('category')
        .not('category', 'is', null);

      if (error) throw error;

      // Benzersiz kategorileri çıkar
      const uniqueCategories = [...new Set(data?.map(p => p.category).filter(Boolean))];

      return { data: uniqueCategories, error: null };
    } catch (error) {
      console.error('getCategories error:', error);
      return { data: null, error };
    }
  },

  /**
   * Kullanılan tüm etiketleri getirir (distinct).
   * @returns {{ data: array|null, error: object|null }}
   */
  async getTags() {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('tags')
        .not('tags', 'is', null);

      if (error) throw error;

      // Tüm tag'leri düzleştir ve benzersiz yap
      const allTags = data?.flatMap(p => p.tags || []) || [];
      const uniqueTags = [...new Set(allTags)];

      return { data: uniqueTags, error: null };
    } catch (error) {
      console.error('getTags error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Blog İstatistikleri
  // ===========================================================

  /**
   * Blog istatistiklerini getirir.
   * @returns {{ data: object|null, error: object|null }}
   */
  async getBlogStats() {
    try {
      const [totalResult, draftResult, publishedResult, archivedResult] = await Promise.all([
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('status', 'draft'),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('status', 'archived'),
      ]);

      return {
        data: {
          total: totalResult.count || 0,
          draft: draftResult.count || 0,
          published: publishedResult.count || 0,
          archived: archivedResult.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('getBlogStats error:', error);
      return { data: null, error };
    }
  },
};

export default blogService;
